import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import type { AccountPracInfoResponseDto, AccountSraDto, PracSiteUpdateDto, PracSraUpdateDto, SkinTypeDefDto } from '../../../types/api';
import { ManageTeamResultsList, type SiteGroup } from './ManageTeamResultsList';
import type { OrderedOption } from './OrderedDualListSelector';
import { ManageTeamSearchBar, type ManageTeamSearchValues } from './ManageTeamSearchBar';
import { SkinTypeDefStorage } from '../../../storage/SkinTypeDefStorage';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { resolveAuthContext } from '../../../services/auth/authContext';
import { AccountPracInfoService } from '../../../services/account/AccountPracInfoService';
import { resolveHiddenSraSettings } from '../../../services/account/SraMenuAccess';
import { isMasterReassignTarget, resolveMaster } from '../../../services/account/SraMasterRules';

interface ManageTeamViewProps {
  accountData?: AccountPracInfoResponseDto | null;
  isLoading?: boolean;
  error?: string | null;
  accountId?: string | null;
  storage?: any;
  apiService?: any;
  /**
   * The account's SkinTypeDef — used for assessment display names in the Office Online
   * (AccessType 30) assessment checkboxes. The assessments themselves come from the practice's
   * PlaList (Account Settings > Manage assessments).
   */
  skinDef?: SkinTypeDefDto | null;
}

interface AppliedFilters {
  email: string;
  site: string;
}

interface SraTypeOption {
  value: string;
  label: string;
}

interface SiteOption {
  siteId: string;
  siteName: string;
}

interface SraUpdateOptions {
  reassignMasterToSraId?: string;
  /**
   * Send the access invitation along with this update: becomes PracSraUpdateDto.SendInvite, the
   * base URL the email's sign-in link should point at. Blank/omitted = no invitation.
   */
  sendInviteBaseUrl?: string;
}

interface MasterRuleResult {
  rows: AccountSraDto[];
  masterRowId: string | null;
  masterWasMissing: boolean;
}

type SiteFormMode = 'edit' | 'add' | null;

const EMPTY_SEARCH: ManageTeamSearchValues = {
  email: '',
  site: '',
};

const UNKNOWN_SITE_LABEL = 'Unknown Site';

// Accounts whose "is there a master?" check + auto-assign has already run this page session — so
// remounting ManageTeamView (the Account view's key changes on several refreshes) never repeats it.
const masterAutofixSettledAccounts = new Set<string>();

const normalize = (value: string): string => value.trim().toLowerCase();

// Rows without an SraId (just-added accesses) are identified by site + email + access type — an
// access is unique by email + name + AccessType, so the same person's Pro and Office Online
// accesses on one site must never collapse into one row.
const getRowId = (row: AccountSraDto): string => {
  return row.SraId || `${row.SiteId}:${row.Email}:${(row.AccessType || '').trim()}`;
};

/** Same SRA? SraIds when both have one, otherwise site + email + access type. */
const isSameSraIdentity = (a: AccountSraDto, b: AccountSraDto): boolean => {
  const aId = (a.SraId || '').trim();
  const bId = (b.SraId || '').trim();
  if (aId && bId) {
    return aId === bId;
  }
  return (a.SiteId || '').trim().toLowerCase() === (b.SiteId || '').trim().toLowerCase()
    && (a.Email || '').trim().toLowerCase() === (b.Email || '').trim().toLowerCase()
    && (a.AccessType || '').trim() === (b.AccessType || '').trim();
};

const MASTER_ACCOUNT_SETTING = 'masteraccount';
const REQUIRED_MASTER_SETTING_NAMES = ['enable_accountmenu', 'enable_manageteams'] as const;

const normalizeSettingName = (value: string): string => value.trim().toLowerCase();

const isTruthySettingValue = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'enabled' || normalized === 'active';
};

const parseAccessTypeValue = (value: string | null | undefined): number => {
  const parsed = Number.parseInt((value || '').trim(), 10);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

const getConfigSettingValue = (configInfo: AccountSraDto['ConfigInfo'], settingName: string): string => {
  const normalized = normalizeSettingName(settingName);
  const match = (configInfo ?? []).find((entry) => normalizeSettingName(entry.Name || '') === normalized);
  return (match?.Value || '').trim();
};

const setConfigSettingValue = (
  configInfo: AccountSraDto['ConfigInfo'],
  settingName: string,
  value: string,
): NonNullable<AccountSraDto['ConfigInfo']> => {
  const normalized = normalizeSettingName(settingName);
  const next = [...(configInfo ?? [])];
  const index = next.findIndex((entry) => normalizeSettingName(entry.Name || '') === normalized);
  if (index >= 0) {
    next[index] = {
      Name: next[index].Name || settingName,
      Value: value,
    };
    return next;
  }

  next.push({
    Name: settingName,
    Value: value,
  });
  return next;
};

const isMasterAccountRow = (row: AccountSraDto): boolean => {
  return isTruthySettingValue(getConfigSettingValue(row.ConfigInfo, MASTER_ACCOUNT_SETTING));
};

const setMasterAccountValue = (row: AccountSraDto, isMaster: boolean): AccountSraDto => {
  return {
    ...row,
    ConfigInfo: setConfigSettingValue(row.ConfigInfo, MASTER_ACCOUNT_SETTING, isMaster ? '1' : '0'),
  };
};

const ensureMasterRequiredConfig = (row: AccountSraDto): AccountSraDto => {
  let nextConfig = [...(row.ConfigInfo ?? [])];
  REQUIRED_MASTER_SETTING_NAMES.forEach((settingName) => {
    nextConfig = setConfigSettingValue(nextConfig, settingName, '1');
  });

  return {
    ...row,
    ConfigInfo: nextConfig,
  };
};

const applyMasterAccountRules = (rows: AccountSraDto[]): MasterRuleResult => {
  if (rows.length === 0) {
    return {
      rows,
      masterRowId: null,
      masterWasMissing: false,
    };
  }

  // Master rules (services/account/SraMasterRules): only a Dashboard (11) — or, when there is no
  // Dashboard, a Pro Dashboard (10) — can be master; a flagged row of any other AccessType is not
  // honoured. masterWasMissing is true only when NO eligible row is flagged.
  const resolution = resolveMaster(rows);
  const masterWasMissing = resolution.masterWasMissing && !!resolution.master;
  const resolvedMasterId = resolution.master ? getRowId(resolution.master) : null;

  const nextRows = rows.map((row) => {
    const isMaster = resolvedMasterId ? getRowId(row) === resolvedMasterId : false;
    if (isMaster) {
      return ensureMasterRequiredConfig(setMasterAccountValue(row, true));
    }
    return setMasterAccountValue(row, false);
  });

  return {
    rows: nextRows,
    masterRowId: resolvedMasterId,
    masterWasMissing,
  };
};

const LAST_LOGIN_KEYS = new Set<string>([
  'lastlogin',
  'last_login',
  'last login',
  'lastsignin',
  'last_signin',
  'last sign in',
  'lastaccess',
  'last_access',
  'last access',
]);

const getNormalizedKey = (value: string): string => {
  return value.trim().toLowerCase();
};

const resolveLastLogin = (row: AccountSraDto): string => {
  if (row.LastLogin && row.LastLogin.trim()) {
    return row.LastLogin.trim();
  }

  const merged = [...(row.ConfigInfo ?? []), ...(row.RemoteInfo ?? [])];
  const match = merged.find((item) => LAST_LOGIN_KEYS.has(getNormalizedKey(item.Name || '')));
  if (!match?.Value || !match.Value.trim()) {
    return '-';
  }
  return match.Value.trim();
};

const resolveRuntimeStorage = (storage?: any) => {
  return storage
    || (globalThis as any).__proGroupsStorage
    || (globalThis as any).__childNavStorage
    || null;
};

const resolveRuntimeApiService = (apiService?: any) => {
  return apiService
    || (globalThis as any).__proGroupsApiService
    || (globalThis as any).__childNavApiService
    || null;
};

const resolveUserStore = (): any => resolveAuthContext().userStore || null;

const resolvePracLanguageTypeId = (accountData?: AccountPracInfoResponseDto | null): string => {
  const localInfo = accountData?.AccountSettings?.PracInfo?.LocalInfo ?? [];
  const languageSetting = localInfo.find((entry) => {
    const key = (entry.Name || '').trim().toLowerCase();
    return key === 'languagetypeid' || key === 'languageid' || key === 'langtype' || key === 'langid';
  });

  const value = (languageSetting?.Value || '').trim();
  return value || '1';
};

const resolveSiteNameById = (siteId: string, siteNameById: Record<string, string>): string => {
  const normalizedSiteId = siteId.trim();
  if (!normalizedSiteId) {
    return UNKNOWN_SITE_LABEL;
  }

  return siteNameById[normalizedSiteId] || normalizedSiteId;
};

// Default/fallback access type labels for common types
const DEFAULT_ACCESS_TYPE_LABELS: Record<string, string> = {
  '10': 'Pro Dashboard',
  '11': 'Dashboard',
  '20': 'Channel',
  '21': 'Account',
  '25': 'System',
  '30': 'Office Online',
};

const SYSTEM_ACCESS_TYPE = '25';

// Hardcoded Add New Access Types offered when the signed-in access type is 25. Account Management
// (21) and Channel Management (20) use the same enable checkboxes as Dashboard in the SRA editor.
const SYSTEM_ADD_ACCESS_TYPE_OPTIONS: SraTypeOption[] = [
  { value: '10', label: 'Pro Dashboard' },
  { value: '11', label: 'Dashboard' },
  { value: '30', label: 'Office Online' },
  { value: '21', label: 'Account Management' },
  { value: '20', label: 'Channel Management' },
];

export const ManageTeamView: React.FC<ManageTeamViewProps> = ({
  accountData,
  isLoading = false,
  error = null,
  accountId,
  storage,
  apiService,
  skinDef = null,
}) => {
  // The practice's assessments — the ones selected in Account Settings > Manage assessments (the
  // PlaList entries that carry an AssessmentId), in the practice's order. Office Online accesses
  // enable/disable from this list. Labels prefer the skin definition's assessment name.
  const assessmentOptions = useMemo<OrderedOption[]>(() => {
    const skinNameById = new Map<string, string>();
    (skinDef?.AssessmentList ?? []).forEach((item) => {
      if (item.AssessmentId && item.DName) {
        skinNameById.set(item.AssessmentId, item.DName);
      }
    });

    const parsePos = (value: string | undefined): number => {
      const parsed = Number.parseInt((value || '').trim(), 10);
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
    };

    const seen = new Set<string>();
    return [...(accountData?.AccountSettings?.PlaList ?? [])]
      .filter((pla) => !!(pla.AssessmentId || '').trim())
      .sort((a, b) => parsePos(a.Pos) - parsePos(b.Pos))
      .filter((pla) => {
        const id = (pla.AssessmentId || '').trim();
        if (seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      })
      .map((pla) => {
        const id = (pla.AssessmentId || '').trim();
        return { id, label: skinNameById.get(id) || pla.DName || id };
      });
  }, [accountData?.AccountSettings?.PlaList, skinDef]);
  // Flags / messaging / schedule management hidden in Account Settings also hide their menus here.
  const hiddenSettings = useMemo(
    () => resolveHiddenSraSettings(accountData?.AccountSettings?.Enables),
    [accountData?.AccountSettings?.Enables]
  );
  const [values, setValues] = useState<ManageTeamSearchValues>(EMPTY_SEARCH);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({ email: '', site: '' });
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [accessTypeDisplayByValue, setAccessTypeDisplayByValue] = useState<Record<string, string>>({});
  const [skinAccessTypeOptions, setAccessTypeOptions] = useState<SraTypeOption[]>([]);
  const [localSraRows, setLocalSraRows] = useState<AccountSraDto[]>([]);
  const [siteNameOverridesById, setSiteNameOverridesById] = useState<Record<string, string>>({});
  const [siteFormMode, setSiteFormMode] = useState<SiteFormMode>(null);
  const [isAddingAccess, setIsAddingAccess] = useState(false);
  const [editingSiteName, setEditingSiteName] = useState<string | null>(null);
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editingSiteNameDraft, setEditingSiteNameDraft] = useState('');
  const [newAccessName, setNewAccessName] = useState('');
  const [newAccessEmail, setNewAccessEmail] = useState('');
  const [newAccessSiteId, setNewAccessSiteId] = useState('');
  const [newAccessType, setNewAccessType] = useState('');
  const [siteUpdateError, setSiteUpdateError] = useState<string | null>(null);
  const [accessFormError, setAccessFormError] = useState<string | null>(null);
  const [isUpdatingSite, setIsUpdatingSite] = useState(false);
  const [isSubmittingAccess, setIsSubmittingAccess] = useState(false);

  const skinType = (accountData?.AccountSettings?.PracInfo?.SkinType || '').trim();
  const languageTypeId = resolvePracLanguageTypeId(accountData);

  useEffect(() => {
    let cancelled = false;

    const loadSkinTypeAccessTypeLabels = async () => {
      if (!skinType) {
        setAccessTypeDisplayByValue(DEFAULT_ACCESS_TYPE_LABELS);
        setAccessTypeOptions([]);
        return;
      }

      const runtimeStorage = resolveRuntimeStorage(storage);
      if (!runtimeStorage) {
        setAccessTypeDisplayByValue(DEFAULT_ACCESS_TYPE_LABELS);
        setAccessTypeOptions([]);
        return;
      }

      try {
        const skinTypeDefStorage = new SkinTypeDefStorage(runtimeStorage);
        const skinTypeDef = await skinTypeDefStorage.getSkinTypeDef(skinType, languageTypeId);
        const fallbackList = !skinTypeDef ? await skinTypeDefStorage.getAllSkinTypeDefs() : [];
        const resolved = skinTypeDef
          || fallbackList.find((entry) => (entry.skinType || '').trim().toLowerCase() === skinType.toLowerCase())
          || null;

        if (cancelled) {
          return;
        }

        if (!resolved) {
          setAccessTypeDisplayByValue(DEFAULT_ACCESS_TYPE_LABELS);
          setAccessTypeOptions([]);
          return;
        }

        const nextOptions = (resolved.sraTypes ?? [])
          .map((entry) => {
            const value = (entry.value || '').trim();
            const label = (entry.name || '').trim();
            if (!value || !label) {
              return null;
            }
            return { value, label };
          })
          .filter((entry): entry is SraTypeOption => !!entry);

        const nextMap = Object.fromEntries(
          nextOptions
            .map((entry) => [entry.value.toLowerCase(), entry.label] as const)
            .filter((pair): pair is readonly [string, string] => !!pair)
        );

        // Merge with default labels (backend labels take precedence)
        const mergedMap = { ...DEFAULT_ACCESS_TYPE_LABELS, ...nextMap };

        setAccessTypeDisplayByValue(mergedMap);
        setAccessTypeOptions(nextOptions);
      } catch (loadError) {
        if (!cancelled) {
          console.warn('ManageTeamView: failed to load SkinTypeDef SraTypes', loadError);
          setAccessTypeDisplayByValue(DEFAULT_ACCESS_TYPE_LABELS);
          setAccessTypeOptions([]);
        }
      }
    };

    void loadSkinTypeAccessTypeLabels();

    return () => {
      cancelled = true;
    };
  }, [languageTypeId, skinType, storage]);

  const sourceRows = useMemo(() => {
    const baseRows = accountData?.SraList ?? [];
    if (localSraRows.length === 0) {
      return baseRows;
    }

    const nextRows = [...baseRows];
    localSraRows.forEach((localRow) => {
      const index = nextRows.findIndex((row) => isSameSraIdentity(row, localRow));

      if (index >= 0) {
        nextRows[index] = localRow;
      } else {
        nextRows.push(localRow);
      }
    });

    return nextRows;
  }, [accountData?.SraList, localSraRows]);

  const masterRuleState = useMemo(() => applyMasterAccountRules(sourceRows), [sourceRows]);
  const effectiveRows = masterRuleState.rows;
  const masterAutofixRef = useRef<string>('');
  const siteNameById = useMemo(() => {
    const map: Record<string, string> = {};
    const siteList = accountData?.SiteList ?? [];

    siteList.forEach((site) => {
      const id = (site.SiteId || '').trim();
      if (!id) {
        return;
      }

      const displayName = (site.SiteDName || '').trim();
      map[id] = displayName || id;
    });

    return map;
  }, [accountData?.SiteList]);

  const siteStatusById = useMemo(() => {
    const map: Record<string, string> = {};
    const siteList = accountData?.SiteList ?? [];

    siteList.forEach((site) => {
      const id = (site.SiteId || '').trim();
      if (!id) {
        return;
      }
      map[id] = (site.Status || '').trim();
    });

    return map;
  }, [accountData?.SiteList]);

  const siteOptionItems = useMemo<SiteOption[]>(() => {
    const list: SiteOption[] = [];
    const seen = new Set<string>();
    (accountData?.SiteList ?? []).forEach((site) => {
      const siteId = (site.SiteId || '').trim();
      if (!siteId || seen.has(siteId)) {
        return;
      }

      seen.add(siteId);
      const siteName = (siteNameOverridesById[siteId] || site.SiteDName || siteId).trim() || siteId;
      list.push({ siteId, siteName });
    });
    return list;
  }, [accountData?.SiteList, siteNameOverridesById]);

  const getSiteDisplayName = (row: AccountSraDto): string => {
    const siteId = (row.SiteId || '').trim();
    if (!siteId) {
      return UNKNOWN_SITE_LABEL;
    }
    const mapped = resolveSiteNameById(siteId, siteNameById);
    return siteNameOverridesById[siteId] || mapped;
  };

  const siteOptions = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    const siteList = accountData?.SiteList ?? [];

    siteList.forEach((site) => {
      const siteId = (site.SiteId || '').trim();
      if (!siteId) {
        return;
      }

      const siteName = (siteNameOverridesById[siteId] || site.SiteDName || siteId).trim();
      if (!siteName || seen.has(siteName)) {
        return;
      }

      seen.add(siteName);
      ordered.push(siteName);
    });

    Object.values(siteNameOverridesById).forEach((siteNameValue) => {
      const siteName = (siteNameValue || '').trim();
      if (!siteName || seen.has(siteName)) {
        return;
      }

      seen.add(siteName);
      ordered.push(siteName);
    });

    return ordered;
  }, [accountData?.SiteList, siteNameOverridesById]);

  const currentLoggedInSraIdentity = useMemo(() => {
    const userStoreHook = resolveUserStore();
    const state = userStoreHook?.getState?.();
    const sessionUser = state?.session?.user;

    const userId = (sessionUser?.userId || '').trim();
    const email = (sessionUser?.email || '').trim();
    const accessType = (sessionUser?.accessType || '').toString().trim();

    return {
      userId,
      email,
      accessType,
    };
  }, []);

  // A System (25) user gets a fixed Type list in Add New Access instead of the skin's SraTypes.
  const accessTypeOptions = useMemo<SraTypeOption[]>(
    () => (currentLoggedInSraIdentity.accessType === SYSTEM_ACCESS_TYPE
      ? SYSTEM_ADD_ACCESS_TYPE_OPTIONS
      : skinAccessTypeOptions),
    [currentLoggedInSraIdentity.accessType, skinAccessTypeOptions]
  );

  // The same person can hold several accesses under one email (e.g. their Pro login and an Office
  // Online access), told apart by AccessType — so an email match only counts as "the signed-in
  // user's own row" when the access types are in the same family (10-19 Pro/Groups, 20-29,
  // 30 Office Online, ...). Unknown access types on either side don't block the match.
  const accessTypesCompatible = (rowAccessType: string | undefined, sessionAccessType: string): boolean => {
    const rowValue = Number.parseInt((rowAccessType || '').trim(), 10);
    const sessionValue = Number.parseInt((sessionAccessType || '').trim(), 10);
    if (!Number.isFinite(rowValue) || !Number.isFinite(sessionValue)) {
      return true;
    }
    return Math.floor(rowValue / 10) === Math.floor(sessionValue / 10);
  };

  const isCurrentLoggedInSraRow = (
    rowLike?: Pick<AccountSraDto, 'SraId' | 'Email' | 'AccessType'> | null
  ): boolean => {
    if (!rowLike) {
      return false;
    }

    const rowSraId = (rowLike.SraId || '').trim();
    const rowEmail = (rowLike.Email || '').trim().toLowerCase();
    const currentSraId = (currentLoggedInSraIdentity.userId || '').trim();
    const currentEmail = (currentLoggedInSraIdentity.email || '').trim().toLowerCase();

    if (rowSraId && currentSraId && rowSraId === currentSraId) {
      return true;
    }

    if (
      rowEmail
      && currentEmail
      && rowEmail === currentEmail
      && accessTypesCompatible(rowLike.AccessType, currentLoggedInSraIdentity.accessType)
    ) {
      return true;
    }

    return false;
  };

  const filteredRows = useMemo(() => {
    const emailFilter = appliedFilters.email;
    const siteFilter = appliedFilters.site;

    if (!emailFilter && !siteFilter) {
      return effectiveRows;
    }

    return effectiveRows.filter((row) => {
      const emailMatches = !emailFilter || normalize(row.Email || '').includes(emailFilter);
      const siteCandidate = getSiteDisplayName(row);
      const siteMatches = !siteFilter || normalize(siteCandidate).includes(siteFilter);
      return emailMatches && siteMatches;
    });
  }, [appliedFilters.email, appliedFilters.site, effectiveRows, siteNameById, siteNameOverridesById]);

  const groupedRows = useMemo<SiteGroup[]>(() => {
    const groups: SiteGroup[] = [];
    const indexes = new Map<string, number>();

    filteredRows.forEach((row) => {
      const siteId = (row.SiteId || '').trim() || UNKNOWN_SITE_LABEL;
      const siteName = getSiteDisplayName(row);
      const existingIndex = indexes.get(siteId);
      if (typeof existingIndex === 'number') {
        groups[existingIndex].rows.push(row);
        return;
      }

      indexes.set(siteId, groups.length);
      groups.push({
        siteId,
        siteName,
        rows: [row],
      });
    });

    return groups;
  }, [filteredRows, siteNameById, siteNameOverridesById]);

  const onSearch = () => {
    setAppliedFilters({
      email: normalize(values.email),
      site: normalize(values.site),
    });
  };

  const onClear = () => {
    setValues(EMPTY_SEARCH);
    setAppliedFilters({ email: '', site: '' });
  };

  const onToggleRow = (rowId: string) => {
    setExpandedRows((current) => ({
      ...current,
      [rowId]: !current[rowId],
    }));
  };

  const onAddNewAccess = () => {
    setSiteFormMode(null);
    setIsAddingAccess(true);
    setEditingSiteName(null);
    setEditingSiteId(null);
    setEditingSiteNameDraft('');
    setSiteUpdateError(null);
    setAccessFormError(null);
    setNewAccessName('');
    setNewAccessEmail('');
    setNewAccessSiteId(siteOptionItems[0]?.siteId || '');
    setNewAccessType(accessTypeOptions[0]?.value || '');
    console.log('ManageTeamView: Add New Access clicked', { accountId });
  };

  const onEditSite = (siteId: string, siteName: string) => {
    setSiteFormMode('edit');
    setEditingSiteName(siteName);
    setEditingSiteId((siteId || '').trim() || null);
    setEditingSiteNameDraft(siteName);
    setSiteUpdateError(null);
    console.log('ManageTeamView: Edit site clicked', { accountId, siteId, siteName });
  };

  const onAddNewSite = (siteId: string, siteName: string) => {
    setSiteFormMode('add');
    setEditingSiteName(null);
    setEditingSiteId((siteId || '').trim() || null);
    setEditingSiteNameDraft('');
    setSiteUpdateError(null);
    console.log('ManageTeamView: Add new site clicked', { accountId, siteId, siteName });
  };

  const onCancelEditSite = () => {
    setSiteFormMode(null);
    setIsAddingAccess(false);
    setEditingSiteName(null);
    setEditingSiteId(null);
    setEditingSiteNameDraft('');
    setSiteUpdateError(null);
    setAccessFormError(null);
    setNewAccessName('');
    setNewAccessEmail('');
    setNewAccessSiteId('');
    setNewAccessType('');
  };

  const onSubmitAddAccess = async () => {
    const trimmedName = newAccessName.trim();
    const trimmedEmail = newAccessEmail.trim();
    const trimmedSiteId = newAccessSiteId.trim();
    const trimmedType = newAccessType.trim();

    if (!trimmedName) {
      setAccessFormError('Name is required.');
      return;
    }

    if (!trimmedEmail) {
      setAccessFormError('Email is required.');
      return;
    }

    if (!trimmedSiteId) {
      setAccessFormError('Site is required.');
      return;
    }

    if (!trimmedType) {
      setAccessFormError('Type is required.');
      return;
    }

    const resolvedAccountId = (accountId || accountData?.AccountId || '').trim();
    if (!resolvedAccountId) {
      setAccessFormError('Missing AccountId for access update.');
      return;
    }

    const resolvedApiService = resolveRuntimeApiService(apiService);
    if (!resolvedApiService?.processServiceRequestWithRefresh) {
      setAccessFormError('API service unavailable for access update.');
      return;
    }

    const payload: PracSraUpdateDto = {
      AccountId: resolvedAccountId,
      Sra: {
        SraId: '',
        AccountId: resolvedAccountId,
        Email: trimmedEmail,
        Name: trimmedName,
        AccessType: trimmedType,
        SiteId: trimmedSiteId,
        SCode: '',
        LastLogin: '',
        Status: '2',
        ConfigInfo: [],
        RemoteInfo: [],
      },
      Status: '2',
    };

    try {
      setIsSubmittingAccess(true);
      setAccessFormError(null);

      const response = await resolvedApiService.processServiceRequestWithRefresh({
        RequestType: 'provider_sra_put',
        Payload: JSON.stringify(payload),
      }, 'sync', {
        userStore: resolveUserStore(),
        storage,
      });

      let responseDto: PracSraUpdateDto | null = null;
      if (response?.payload) {
        try {
          responseDto = JSON.parse(response.payload) as PracSraUpdateDto;
        } catch {
          responseDto = null;
        }
      }

      const responseSra = responseDto?.Sra;
      const finalSra: AccountSraDto = {
        SraId: (responseSra?.SraId || payload.Sra.SraId || '').trim(),
        AccountId: (responseSra?.AccountId || payload.Sra.AccountId || resolvedAccountId).trim(),
        Email: (responseSra?.Email || payload.Sra.Email || trimmedEmail).trim(),
        Name: (responseSra?.Name || payload.Sra.Name || trimmedName).trim(),
        AccessType: (responseSra?.AccessType || payload.Sra.AccessType || trimmedType).trim(),
        SiteId: (responseSra?.SiteId || payload.Sra.SiteId || trimmedSiteId).trim(),
        SCode: (responseSra?.SCode || payload.Sra.SCode || '').trim(),
        LastLogin: (responseSra?.LastLogin || payload.Sra.LastLogin || '').trim(),
        Status: (responseSra?.Status || responseDto?.Status || payload.Status || '2').trim() || '2',
        ConfigInfo: responseSra?.ConfigInfo ?? payload.Sra.ConfigInfo,
        RemoteInfo: responseSra?.RemoteInfo ?? payload.Sra.RemoteInfo,
      };

      setLocalSraRows((current) => {
        const index = current.findIndex((row) => isSameSraIdentity(row, finalSra));

        if (index >= 0) {
          const next = [...current];
          next[index] = finalSra;
          return next;
        }

        return [...current, finalSra];
      });

      setExpandedRows((current) => ({
        ...current,
        [getRowId(finalSra)]: true,
      }));

      const runtimeStorage = resolveRuntimeStorage(storage);
      if (runtimeStorage) {
        try {
          const accountPracInfoStorage = new AccountPracInfoStorage(runtimeStorage);
          const cached = await accountPracInfoStorage.getAccountPracInfo(resolvedAccountId);
          if (cached) {
            const nextSraList = [...(cached.SraList ?? [])];
            const existingIndex = nextSraList.findIndex((row) => isSameSraIdentity(row, finalSra));

            if (existingIndex >= 0) {
              nextSraList[existingIndex] = finalSra;
            } else {
              nextSraList.push(finalSra);
            }

            await accountPracInfoStorage.saveAccountPracInfo(resolvedAccountId, {
              ...cached,
              SraList: nextSraList,
            });
          }
        } catch (cacheError) {
          console.warn('ManageTeamView: failed to persist SraList cache after provider_sra_put', cacheError);
        }
      }

      console.log('ManageTeamView: provider_sra_put succeeded', {
        accountId: resolvedAccountId,
        sraId: finalSra.SraId,
        email: finalSra.Email,
        siteId: finalSra.SiteId,
        accessType: finalSra.AccessType,
        status: finalSra.Status,
      });

      onCancelEditSite();
    } catch (updateError) {
      const message = updateError instanceof Error ? updateError.message : String(updateError);
      setAccessFormError(message);
      console.warn('ManageTeamView: provider_sra_put failed', updateError);
    } finally {
      setIsSubmittingAccess(false);
    }
  };

  const onUpdateSra = async (nextRow: AccountSraDto, options: SraUpdateOptions = {}): Promise<void> => {
    const resolvedAccountId = (accountId || accountData?.AccountId || nextRow.AccountId || '').trim();
    if (!resolvedAccountId) {
      throw new Error('Missing AccountId for SRA update.');
    }

    const resolvedApiService = resolveRuntimeApiService(apiService);
    if (!resolvedApiService?.processServiceRequestWithRefresh) {
      throw new Error('API service unavailable for SRA update.');
    }

    const isEditingMaster = !!masterRuleState.masterRowId && getRowId(nextRow) === masterRuleState.masterRowId;
    let sanitizedRow = { ...nextRow };
    if (isEditingMaster) {
      sanitizedRow = ensureMasterRequiredConfig(setMasterAccountValue(sanitizedRow, true));
      const parsedStatus = Number.parseInt((sanitizedRow.Status || '').trim(), 10);
      if (!Number.isFinite(parsedStatus) || parsedStatus <= 0) {
        sanitizedRow.Status = '1';
      }
    }

    const reassignmentTargetId = (options.reassignMasterToSraId || '').trim();
    const shouldReassignMaster = isEditingMaster
      && isCurrentSignedInMaster
      && !!reassignmentTargetId;

    if (shouldReassignMaster) {
      sanitizedRow = setMasterAccountValue(sanitizedRow, false);
    }

    const payload: PracSraUpdateDto = {
      AccountId: resolvedAccountId,
      Sra: {
        ...sanitizedRow,
        AccountId: resolvedAccountId,
      },
      // Blank = no invitation (an ordinary update); otherwise the base URL for the email's link.
      SendInvite: (options.sendInviteBaseUrl || '').trim(),
      Status: (sanitizedRow.Status || '1').trim() || '1',
    };

    const response = await resolvedApiService.processServiceRequestWithRefresh({
      RequestType: 'provider_sra_put',
      Payload: JSON.stringify(payload),
    }, 'sync', {
      userStore: resolveUserStore(),
      storage,
    });

    let responseDto: PracSraUpdateDto | null = null;
    if (response?.payload) {
      try {
        responseDto = JSON.parse(response.payload) as PracSraUpdateDto;
      } catch {
        responseDto = null;
      }
    }

    const responseSra = responseDto?.Sra;
    const finalSra: AccountSraDto = {
      SraId: (responseSra?.SraId || payload.Sra.SraId || '').trim(),
      AccountId: (responseSra?.AccountId || payload.Sra.AccountId || resolvedAccountId).trim(),
      Email: (responseSra?.Email || payload.Sra.Email || '').trim(),
      Name: (responseSra?.Name || payload.Sra.Name || '').trim(),
      AccessType: (responseSra?.AccessType || payload.Sra.AccessType || '').trim(),
      SiteId: (responseSra?.SiteId || payload.Sra.SiteId || '').trim(),
      SCode: (responseSra?.SCode || payload.Sra.SCode || '').trim(),
      LastLogin: (responseSra?.LastLogin || payload.Sra.LastLogin || '').trim(),
      Status: (responseSra?.Status || responseDto?.Status || payload.Status || '1').trim() || '1',
      ConfigInfo: responseSra?.ConfigInfo ?? payload.Sra.ConfigInfo,
      RemoteInfo: responseSra?.RemoteInfo ?? payload.Sra.RemoteInfo,
    };

    const finalNormalizedSra = isEditingMaster && !shouldReassignMaster
      ? ensureMasterRequiredConfig(setMasterAccountValue(finalSra, true))
      : finalSra;

    const finalStatusNumeric = Number.parseInt(finalSra.Status || '0', 10);
    const shouldRemoveSra = Number.isFinite(finalStatusNumeric) && finalStatusNumeric <= -2;
    // The row being updated, matched by SraId or — for accesses without one yet — by site +
    // email + access type (see isSameSraIdentity: never collapse a person's Pro and Office
    // Online accesses on the same site into one).
    const targetSra: AccountSraDto = {
      ...finalNormalizedSra,
      SraId: (payload.Sra.SraId || finalNormalizedSra.SraId || '').trim(),
      SiteId: (payload.Sra.SiteId || finalNormalizedSra.SiteId || '').trim(),
      Email: (payload.Sra.Email || finalNormalizedSra.Email || '').trim(),
      AccessType: (payload.Sra.AccessType || finalNormalizedSra.AccessType || '').trim(),
    };
    const isTargetSraRow = (row: AccountSraDto): boolean => isSameSraIdentity(row, targetSra);

    setLocalSraRows((current) => {
      const index = current.findIndex(isTargetSraRow);

      if (shouldRemoveSra) {
        if (index < 0) {
          return current;
        }

        return current.filter((row, rowIndex) => rowIndex !== index);
      }

      if (index >= 0) {
        const next = [...current];
        next[index] = finalNormalizedSra;
        return next;
      }

      return [...current, finalNormalizedSra];
    });

    // Only an edit to the signed-in user's OWN access record changes the menus. That is decided
    // against the cached SraUser (the record the shell builds its tabs from): same SraId, or
    // — with no ids — same site + email + access type. The email-based session heuristic is NOT
    // used here: the same person can hold several accesses under one email, and treating an
    // edited Pro/Office access as "me" overwrote the signed-in user's menu settings with the
    // edited row's and dropped tabs (e.g. Manage Team).
    let shouldRefreshSraVisibility = false;

    const runtimeStorage = resolveRuntimeStorage(storage);
    if (runtimeStorage) {
      try {
        const accountPracInfoStorage = new AccountPracInfoStorage(runtimeStorage);
        const cached = await accountPracInfoStorage.getAccountPracInfo(resolvedAccountId);
        if (cached) {
          shouldRefreshSraVisibility = !!cached.SraUser
            && !shouldRemoveSra
            && isSameSraIdentity(cached.SraUser, targetSra);

          const nextCached = shouldRefreshSraVisibility && cached.SraUser
            ? {
              ...cached,
              SraUser: {
                ...cached.SraUser,
                ConfigInfo: finalSra.ConfigInfo ?? cached.SraUser.ConfigInfo,
              },
            }
            : cached;

          const nextSraList = [...(cached.SraList ?? [])];
          const existingIndex = nextSraList.findIndex(isTargetSraRow);

          if (shouldRemoveSra) {
            if (existingIndex >= 0) {
              nextSraList.splice(existingIndex, 1);
            }
          } else if (existingIndex >= 0) {
            nextSraList[existingIndex] = finalNormalizedSra;
          } else {
            nextSraList.push(finalNormalizedSra);
          }

          await accountPracInfoStorage.saveAccountPracInfo(resolvedAccountId, {
            ...nextCached,
            SraList: nextSraList,
          });
        }
      } catch (cacheError) {
        console.warn('ManageTeamView: failed to persist SraList cache after provider_sra_put update', cacheError);
      }
    }

    if (shouldRefreshSraVisibility && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      // sra-menu-updated re-reads the cached SRA and refreshes the menus WITHOUT remounting the
      // Account view (progroups:sra-config-updated force-remounts it, which would collapse the
      // open edit area).
      window.dispatchEvent(new CustomEvent('progroups:sra-menu-updated', {
        detail: {
          accountId: resolvedAccountId,
        },
      }));
    }

    if (shouldReassignMaster) {
      const promoteTarget = effectiveRows.find((row) => (row.SraId || '').trim() === reassignmentTargetId);
      if (!promoteTarget) {
        throw new Error('Selected master reassignment target was not found.');
      }
      if (!isMasterReassignTarget(promoteTarget)) {
        throw new Error('The master account can only be reassigned to a Dashboard access.');
      }

      const promotedTargetRow = ensureMasterRequiredConfig(setMasterAccountValue({
        ...promoteTarget,
        Status: (promoteTarget.Status || '1').trim() || '1',
      }, true));

      await onUpdateSra(promotedTargetRow);
    }
  };

  const onSubmitSiteForm = async () => {
    const mode = siteFormMode;
    const original = (editingSiteName || '').trim();
    const siteId = (editingSiteId || '').trim();
    const nextName = editingSiteNameDraft.trim();

    if (mode === 'edit') {
      if (!original) {
        onCancelEditSite();
        return;
      }

      if (!nextName || nextName === original) {
        onCancelEditSite();
        return;
      }

      if (!siteId) {
        setSiteUpdateError('Missing SiteId for update.');
        return;
      }
    } else if (mode === 'add') {
      if (!nextName) {
        setSiteUpdateError('New Site Name is required.');
        return;
      }
    } else {
      return;
    }

    const resolvedAccountId = (accountId || accountData?.AccountId || '').trim();
    if (!resolvedAccountId) {
      setSiteUpdateError('Missing AccountId for update.');
      return;
    }

    const resolvedApiService = resolveRuntimeApiService(apiService);
    if (!resolvedApiService?.processServiceRequestWithRefresh) {
      setSiteUpdateError('API service unavailable for site update.');
      return;
    }

    const payload: PracSiteUpdateDto = {
      AccountId: resolvedAccountId,
      SiteId: mode === 'add' ? (siteId || '0') : siteId,
      DName: nextName,
      Status: mode === 'add' ? '2' : (siteStatusById[siteId] || '1'),
    };

    try {
      setIsUpdatingSite(true);
      setSiteUpdateError(null);

      const response = await resolvedApiService.processServiceRequestWithRefresh({
        RequestType: 'provider_site_put',
        Payload: JSON.stringify(payload),
      }, 'sync', {
        userStore: resolveUserStore(),
        storage,
      });

      let responseDto: PracSiteUpdateDto | null = null;
      if (response?.payload) {
        try {
          responseDto = JSON.parse(response.payload) as PracSiteUpdateDto;
        } catch {
          responseDto = null;
        }
      }

      const finalName = (responseDto?.DName || nextName).trim() || nextName;
      const finalStatus = (responseDto?.Status || payload.Status || siteStatusById[siteId] || '1').trim() || '1';
      const finalSiteId = (responseDto?.SiteId || payload.SiteId || '').trim();

      if (mode === 'edit') {
        setSiteNameOverridesById((current) => ({
          ...current,
          [siteId]: finalName,
        }));
      } else if (finalSiteId) {
        setSiteNameOverridesById((current) => ({
          ...current,
          [finalSiteId]: finalName,
        }));
      }

      const runtimeStorage = resolveRuntimeStorage(storage);
      if (runtimeStorage) {
        try {
          const accountPracInfoStorage = new AccountPracInfoStorage(runtimeStorage);
          const cached = await accountPracInfoStorage.getAccountPracInfo(resolvedAccountId);

          if (cached) {
            let nextSiteList = cached.SiteList ?? [];

            if (mode === 'edit') {
              nextSiteList = nextSiteList.map((site) => {
                if ((site.SiteId || '').trim() !== siteId) {
                  return site;
                }

                return {
                  ...site,
                  SiteDName: finalName,
                  Status: finalStatus,
                };
              });
            } else if (finalSiteId) {
              const existingIndex = nextSiteList.findIndex((site) => (site.SiteId || '').trim() === finalSiteId);
              if (existingIndex >= 0) {
                nextSiteList = nextSiteList.map((site, index) => {
                  if (index !== existingIndex) {
                    return site;
                  }
                  return {
                    ...site,
                    SiteDName: finalName,
                    Status: finalStatus,
                  };
                });
              } else {
                nextSiteList = [...nextSiteList, {
                  SiteId: finalSiteId,
                  SiteDName: finalName,
                  Status: finalStatus,
                }];
              }
            }

            await accountPracInfoStorage.saveAccountPracInfo(resolvedAccountId, {
              ...cached,
              SiteList: nextSiteList,
            });
          }
        } catch (cacheError) {
          console.warn('ManageTeamView: failed to persist SiteList cache after provider_site_put', cacheError);
        }
      }

      console.log('ManageTeamView: Update site clicked', {
        mode,
        accountId: resolvedAccountId,
        siteId: finalSiteId || siteId,
        originalSiteName: original,
        nextSiteName: finalName,
        nextStatus: finalStatus,
      });
      onCancelEditSite();
    } catch (updateError) {
      const message = updateError instanceof Error ? updateError.message : String(updateError);
      setSiteUpdateError(message);
      console.warn('ManageTeamView: provider_site_put failed', updateError);
    } finally {
      setIsUpdatingSite(false);
    }
  };

  // Auto-assign a master ONLY when the account truly has none. The check that "none is set" must be
  // complete before this ever fires, so it is gated three ways:
  //   1. the SRA list must be fully loaded here (not loading, no error, every row's ConfigInfo
  //      present — a partially loaded list has no master flags and looked like "no master");
  //   2. the local view says no eligible master is flagged; and then
  //   3. a FRESH AccountSras read from the backend is re-evaluated — if it shows a master the
  //      cache is just refreshed and nothing is assigned. A failed read never assigns.
  // It runs at most once per account per page session (module-level set), so remounts and
  // re-renders can't repeat it.
  useEffect(() => {
    const resolvedAccountId = (accountId || accountData?.AccountId || '').trim();
    if (!resolvedAccountId || isLoading || error) {
      return;
    }

    if (!masterRuleState.masterWasMissing || !masterRuleState.masterRowId) {
      return;
    }

    const loadedRows = accountData?.SraList;
    if (!Array.isArray(loadedRows) || loadedRows.length === 0) {
      return;
    }
    if (loadedRows.some((row) => !Array.isArray(row.ConfigInfo))) {
      return;
    }

    if (masterAutofixSettledAccounts.has(resolvedAccountId) || masterAutofixRef.current) {
      return;
    }

    const resolvedApiService = resolveRuntimeApiService(apiService);
    if (!resolvedApiService) {
      return;
    }

    masterAutofixRef.current = resolvedAccountId;

    void (async () => {
      try {
        const freshDto = await new AccountPracInfoService(resolvedApiService).getAccountPracInfo({
          AccountId: resolvedAccountId,
          Types: ['AccountSras'],
        }, {
          userStore: resolveUserStore(),
          storage: resolveRuntimeStorage(storage) ?? storage,
        });

        const freshRows = freshDto?.SraList;
        if (!Array.isArray(freshRows) || freshRows.length === 0 || freshRows.some((row) => !Array.isArray(row.ConfigInfo))) {
          return; // can't prove there is no master — leave everything alone
        }

        // Bring the cached list up to date either way.
        const runtimeStorage = resolveRuntimeStorage(storage);
        if (runtimeStorage) {
          try {
            const cacheStorage = new AccountPracInfoStorage(runtimeStorage);
            const cached = await cacheStorage.getAccountPracInfo(resolvedAccountId);
            if (cached) {
              await cacheStorage.saveAccountPracInfo(resolvedAccountId, { ...cached, SraList: freshRows });
            }
          } catch (cacheError) {
            console.warn('ManageTeamView: failed to refresh SraList cache during master check', cacheError);
          }
        }

        const fresh = resolveMaster(freshRows);

        if (fresh.masterWasMissing && fresh.master) {
          const normalizedMasterRow = ensureMasterRequiredConfig(setMasterAccountValue(fresh.master, true));
          await onUpdateSra(normalizedMasterRow);
        }

        // No other access type may carry the master flag — clear any stale one.
        for (const staleRow of fresh.ineligibleFlaggedRows) {
          await onUpdateSra(setMasterAccountValue(staleRow, false));
        }

        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('progroups:sra-menu-updated', {
            detail: { accountId: resolvedAccountId },
          }));
        }
      } catch (autofixError) {
        console.warn('ManageTeamView: master account check/auto-assign failed', autofixError);
      } finally {
        masterAutofixSettledAccounts.add(resolvedAccountId);
        masterAutofixRef.current = '';
      }
    })();
  }, [
    accountData?.AccountId,
    accountData?.SraList,
    accountId,
    apiService,
    error,
    isLoading,
    masterRuleState.masterRowId,
    masterRuleState.masterWasMissing,
    storage,
  ]);

  const expandedRowIds = useMemo(() => {
    if (Object.keys(expandedRows).length === 0) {
      return expandedRows;
    }

    const visibleIds = new Set(filteredRows.map((row) => getRowId(row)));
    const next: Record<string, boolean> = {};
    Object.keys(expandedRows).forEach((id) => {
      if (visibleIds.has(id) && expandedRows[id]) {
        next[id] = true;
      }
    });
    return next;
  }, [expandedRows, filteredRows]);

  const resolveAccessTypeDisplay = (row: AccountSraDto): string => {
    const raw = (row.AccessType || '').trim();
    if (!raw) {
      return '-';
    }

    const baseLabel = accessTypeDisplayByValue[raw.toLowerCase()] || raw;
    return isMasterAccountRow(row) ? `${baseLabel} *` : baseLabel;
  };

  const masterReassignCandidates = useMemo(() => {
    const masterRowId = masterRuleState.masterRowId;
    return effectiveRows
      .filter((row) => {
        const sraId = (row.SraId || '').trim();
        if (!sraId) {
          return false;
        }
        if (masterRowId && getRowId(row) === masterRowId) {
          return false;
        }
        // Reassign targets are Dashboards (AccessType 11) only.
        return isMasterReassignTarget(row);
      })
      .map((row) => ({
        sraId: (row.SraId || '').trim(),
        label: `${(row.Name || row.Email || row.SraId || 'Access').trim()} (${resolveAccessTypeDisplay(row)})`,
      }));
  }, [effectiveRows, masterRuleState.masterRowId]);

  const isCurrentSignedInMaster = useMemo(() => {
    if (!masterRuleState.masterRowId) {
      return false;
    }
    const masterRow = effectiveRows.find((row) => getRowId(row) === masterRuleState.masterRowId);
    return isCurrentLoggedInSraRow(masterRow);
  }, [effectiveRows, masterRuleState.masterRowId]);

  useEffect(() => {
    if (!isAddingAccess) {
      return;
    }

    if (!newAccessSiteId && siteOptionItems.length > 0) {
      setNewAccessSiteId(siteOptionItems[0].siteId);
    }

    if (!newAccessType && accessTypeOptions.length > 0) {
      setNewAccessType(accessTypeOptions[0].value);
    }
  }, [accessTypeOptions, isAddingAccess, newAccessSiteId, newAccessType, siteOptionItems]);

  const isWeb = Platform.OS === 'web';
  const isSiteFormVisible = siteFormMode === 'edit' || siteFormMode === 'add';
  const isAddSiteMode = siteFormMode === 'add';
  const accessSiteValues = siteOptionItems;
  const accessTypeValues = accessTypeOptions.map((option) => option.value);
  const resolveSiteNameByOptionId = (siteId: string): string => {
    return accessSiteValues.find((entry) => entry.siteId === siteId)?.siteName || siteId;
  };
  const resolveAccessTypeLabelByValue = (value: string): string => {
    return accessTypeOptions.find((option) => option.value === value)?.label || value;
  };
  const cycleAccessSite = () => {
    if (accessSiteValues.length === 0) {
      return;
    }

    const currentIndex = accessSiteValues.findIndex((entry) => entry.siteId === newAccessSiteId);
    const nextIndex = currentIndex < 0 || currentIndex === accessSiteValues.length - 1 ? 0 : currentIndex + 1;
    setNewAccessSiteId(accessSiteValues[nextIndex].siteId);
  };
  const cycleAccessType = () => {
    if (accessTypeValues.length === 0) {
      return;
    }

    const currentIndex = accessTypeValues.indexOf(newAccessType);
    const nextIndex = currentIndex < 0 || currentIndex === accessTypeValues.length - 1 ? 0 : currentIndex + 1;
    setNewAccessType(accessTypeValues[nextIndex]);
  };
  const webDropdownStyle = { ...webControlStyle, width: '100%', height: 28 } as any;

  return (
    <View style={styles.container}>
      {isSiteFormVisible ? (
        <View style={styles.siteEditContainer}>
          <View style={styles.siteEditGrid}>
            <View style={styles.siteEditCellCol1}>
              <Text style={styles.siteEditLabel}>{isAddSiteMode ? 'New Site Name' : 'Edit Site Name'}</Text>
            </View>
            <View style={styles.siteEditCellCol2}>
              <TextInput
                style={styles.siteEditInput}
                value={editingSiteNameDraft}
                onChangeText={setEditingSiteNameDraft}
                autoCapitalize="words"
                placeholder={isAddSiteMode ? 'New site name' : 'Site name'}
                placeholderTextColor={EDITOR_COLORS.placeholder}
              />
            </View>
            <View style={styles.siteEditCellCol3}>
              <Pressable
                style={[styles.siteUpdateBtn, isUpdatingSite && styles.siteUpdateBtnDisabled]}
                onPress={onSubmitSiteForm}
                disabled={isUpdatingSite}
                accessibilityRole="button"
                accessibilityLabel={isAddSiteMode ? 'Add Site' : 'Update Site'}
              >
                <Text style={styles.siteUpdateBtnText}>{isAddSiteMode ? 'Add Site' : 'Update Site'}</Text>
              </Pressable>
            </View>

            <View style={styles.siteEditCellCol1} />
            <View style={styles.siteEditCellCol2} />
            <View style={styles.siteEditCellCol3}>
              <Pressable onPress={onCancelEditSite} accessibilityRole="button" accessibilityLabel="Cancel site edit" style={styles.siteCancelTouch}>
                <Text style={styles.siteCancelText}>Cancel</Text>
              </Pressable>
            </View>

            {siteUpdateError ? (
              <View style={styles.siteEditErrorRow}>
                <Text style={styles.siteEditErrorText}>{siteUpdateError}</Text>
              </View>
            ) : null}
          </View>
        </View>
      ) : isAddingAccess ? (
        <View style={styles.siteEditContainer}>
          <View style={styles.siteEditGrid}>
            {/* Email first, then Name — same order as the sign-in form. */}
            <View style={styles.siteEditCellCol1}>
              <Text style={styles.siteEditLabel}>Email</Text>
            </View>
            <View style={styles.siteEditCellCol2}>
              <TextInput
                style={styles.siteEditInput}
                value={newAccessEmail}
                onChangeText={setNewAccessEmail}
                autoCapitalize="none"
                keyboardType="email-address"
                placeholder="Email"
                placeholderTextColor={EDITOR_COLORS.placeholder}
              />
            </View>
            <View style={styles.siteEditCellCol3}>
              <Pressable
                style={[styles.siteUpdateBtn, styles.addAccessBtnWide, isSubmittingAccess && styles.siteUpdateBtnDisabled]}
                onPress={onSubmitAddAccess}
                disabled={isSubmittingAccess}
                accessibilityRole="button"
                accessibilityLabel="Add Access Now"
              >
                <Text style={styles.siteUpdateBtnText}>Add Access Now</Text>
              </Pressable>
            </View>

            <View style={styles.siteEditCellCol1}>
              <Text style={styles.siteEditLabel}>Name</Text>
            </View>
            <View style={styles.siteEditCellCol2}>
              <TextInput
                style={styles.siteEditInput}
                value={newAccessName}
                onChangeText={setNewAccessName}
                autoCapitalize="words"
                placeholder="Name"
                placeholderTextColor={EDITOR_COLORS.placeholder}
              />
            </View>
            <View style={styles.siteEditCellCol3} />

            <View style={styles.siteEditCellCol1}>
              <Text style={styles.siteEditLabel}>Site</Text>
            </View>
            <View style={styles.siteEditCellCol2}>
              {isWeb ? (
                React.createElement(
                  'select' as any,
                  {
                    value: newAccessSiteId,
                    onChange: (event: any) => setNewAccessSiteId(String(event?.target?.value ?? '')),
                    style: webDropdownStyle,
                    'aria-label': 'Access site',
                  },
                  accessSiteValues.map((option) => (
                    React.createElement('option' as any, { key: option.siteId, value: option.siteId }, option.siteName)
                  ))
                )
              ) : (
                <Pressable onPress={cycleAccessSite} style={styles.siteBtnLike} accessibilityRole="button" accessibilityLabel="Access site">
                  <Text style={styles.siteBtnLikeText} numberOfLines={1}>{resolveSiteNameByOptionId(newAccessSiteId)}</Text>
                  <Text style={styles.siteBtnLikeChevron}>▼</Text>
                </Pressable>
              )}
            </View>
            <View style={styles.siteEditCellCol3} />

            <View style={styles.siteEditCellCol1}>
              <Text style={styles.siteEditLabel}>Type</Text>
            </View>
            <View style={styles.siteEditCellCol2}>
              {isWeb ? (
                React.createElement(
                  'select' as any,
                  {
                    value: newAccessType,
                    onChange: (event: any) => setNewAccessType(String(event?.target?.value ?? '')),
                    style: webDropdownStyle,
                    'aria-label': 'Access type',
                  },
                  accessTypeOptions.map((option) => (
                    React.createElement('option' as any, { key: option.value, value: option.value }, option.label)
                  ))
                )
              ) : (
                <Pressable onPress={cycleAccessType} style={styles.siteBtnLike} accessibilityRole="button" accessibilityLabel="Access type">
                  <Text style={styles.siteBtnLikeText} numberOfLines={1}>{resolveAccessTypeLabelByValue(newAccessType)}</Text>
                  <Text style={styles.siteBtnLikeChevron}>▼</Text>
                </Pressable>
              )}
            </View>
            <View style={styles.siteEditCellCol3}>
              <Pressable onPress={onCancelEditSite} accessibilityRole="button" accessibilityLabel="Cancel add access" style={styles.siteCancelTouch}>
                <Text style={styles.siteCancelText}>Cancel</Text>
              </Pressable>
            </View>

            {accessFormError ? (
              <View style={styles.siteEditErrorRow}>
                <Text style={styles.siteEditErrorText}>{accessFormError}</Text>
              </View>
            ) : null}
          </View>
        </View>
      ) : (
        <ManageTeamSearchBar
          values={values}
          onValuesChange={setValues}
          onSearch={onSearch}
          onClear={onClear}
          onAddNewAccess={onAddNewAccess}
          siteOptions={siteOptions}
          isLoading={isLoading}
        />
      )}

      <ManageTeamResultsList
        groups={groupedRows}
        isLoading={isLoading}
        error={error}
        expandedRowIds={expandedRowIds}
        onToggleRow={onToggleRow}
        onResolveLastLogin={resolveLastLogin}
        onResolveAccessType={resolveAccessTypeDisplay}
        onEditSite={onEditSite}
        onAddNewSite={onAddNewSite}
        onUpdateSra={onUpdateSra}
        siteOptions={siteOptionItems}
        currentLoggedInSraId={currentLoggedInSraIdentity.userId}
        currentLoggedInSraEmail={currentLoggedInSraIdentity.email}
        currentLoggedInAccessType={currentLoggedInSraIdentity.accessType}
        masterRowId={masterRuleState.masterRowId}
        canReassignMaster={isCurrentSignedInMaster}
        masterReassignCandidates={masterReassignCandidates}
        assessmentOptions={assessmentOptions}
        hiddenSettings={hiddenSettings}
      />

    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    padding: 12,
    backgroundColor: EDITOR_COLORS.background,
  },
  siteEditContainer: {
    width: '100%',
    backgroundColor: EDITOR_COLORS.background,
    borderRadius: 14,
    padding: 16,
    marginBottom: 18,
    alignItems: 'center',
  },
  siteEditGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 650,
    rowGap: 4,
  },
  siteEditCellCol1: {
    width: '20%',
    alignItems: 'flex-end',
    justifyContent: 'center',
    minHeight: 25,
    paddingRight: 10,
  },
  siteEditCellCol2: {
    width: '49%',
    alignItems: 'stretch',
    justifyContent: 'center',
    minHeight: 25,
    paddingRight: 8,
  },
  siteEditCellCol3: {
    width: '20%',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 25,
  },
  siteEditLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  siteEditInput: {
    ...editorText,
    height: 28,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  siteUpdateBtn: {
    width: 110,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  siteUpdateBtnDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  siteUpdateBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  addAccessBtnWide: {
    width: 130,
  },
  siteCancelTouch: {
    marginTop: 0,
    paddingVertical: 0,
    paddingHorizontal: 4,
  },
  siteCancelText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    textDecorationLine: 'none',
  },
  siteBtnLike: {
    height: 28,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    backgroundColor: EDITOR_COLORS.background,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  siteBtnLikeText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
    marginRight: 6,
  },
  siteBtnLikeChevron: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  siteEditErrorRow: {
    width: '100%',
    marginTop: 4,
    alignItems: 'center',
  },
  siteEditErrorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
  },
  summaryText: {
    ...editorText,
    marginTop: 10,
    color: EDITOR_COLORS.textMuted,
    textAlign: 'right',
  },
});
